import { Cache, pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import type { Function } from './function'
import { MethodName } from './method-name'
import { PASS_THROUGH, type PassThroughIfPossible } from './pass-through'
import type { Transformer } from './transformer'

// TODO: maybe remove transformer arguments and encourage pipe instead. E.g.
// `factory.validate(factory.select('a', factory.select('b', PASS_THROUGH)),
// TypeAsserter.assertIsString)` => `factory.pipe(factory.select('a', 'b'),
// factory.validate(TypeAsserter.assertIsString))` (NOTE: factory.select could
// be refactored to accept ...path: K[])

/**
 * Can be used to create transformers for a given output type
 */
export class Factory<TOutputType extends string> {
  /**
   * A {@link Transformer} created by this {@link Factory} will have its
   * {@link Function} at this {@link MethodName}
   */
  public readonly methodName: MethodName<TOutputType>

  /**
   * Does this transformer have the appropriate transformation method?
   */
  // NOTE: lazy is used to ensure there are no issues with this being defined
  // before `methodName`
  public canUse = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Transformer<TOutputType>>({
      [this.methodName]: TypeChecker.isFunction as TypeChecker<
        Transformer<TOutputType>[MethodName<TOutputType>]
      >,
    } satisfies TypeChecker.Factory.object.Fields<Transformer> as TypeChecker.Factory.object.Fields<
      Transformer<TOutputType>
    >),
  ).usingAlias(() => `Transformer<${this.outputType}>`)

  /**
   * Ensure that this transformer has the appropriate transformation method
   */
  public assertCanUse: TypeAsserter<Transformer<TOutputType>> =
    TypeAsserter.Factory.from(this.canUse)

  constructor(public readonly outputType: TOutputType) {
    this.methodName = MethodName.from(this.outputType)
  }

  /**
   * Create a transformer with custom logic
   *
   * __NOTE:__ there is usually another factory method that can do what you are
   * trying to do and is more robust
   */
  public custom<TIn, TOut>(
    fn: Function<TIn, TOut>,
  ): Transformer<TOutputType, TIn, TOut> {
    return {
      [this.methodName]: fn,
    } satisfies Transformer<any, TIn, TOut> as Transformer<
      TOutputType,
      TIn,
      TOut
    >
  }

  /**
   * Combines {@link Transformer Transformers} by passing the output of one as
   * the input to the next
   *
   * @example
   * const fullTransformer = factory.pipe(
   *   // Try and transform each field
   *   factory.object<Input, Partial<MyItem>>(...),
   *   // If any required field is missing, make the whole return type undefined
   *   factory.if(MyItem.isMyItem, PASS_THROUGH, factory.always(undefined))
   * )
   */
  public pipe<TIn>(): Transformer<TOutputType, TIn, TIn>
  public pipe<TIn, TOut>(
    t1: Transformer<TOutputType, TIn, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe<TIn, TMid1, TOut>(
    t1: Transformer<TOutputType, TIn, TMid1>,
    t2: Transformer<TOutputType, TMid1, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe<TIn, TMid1, TMid2, TOut>(
    t1: Transformer<TOutputType, TIn, TMid1>,
    t2: Transformer<TOutputType, TMid1, TMid2>,
    t3: Transformer<TOutputType, TMid2, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe<TIn, TMid1, TMid2, TMid3, TOut>(
    t1: Transformer<TOutputType, TIn, TMid1>,
    t2: Transformer<TOutputType, TMid1, TMid2>,
    t3: Transformer<TOutputType, TMid2, TMid3>,
    t4: Transformer<TOutputType, TMid3, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe<TIn, TMid1, TMid2, TMid3, TMid4, TOut>(
    t1: Transformer<TOutputType, TIn, TMid1>,
    t2: Transformer<TOutputType, TMid1, TMid2>,
    t3: Transformer<TOutputType, TMid2, TMid3>,
    t4: Transformer<TOutputType, TMid3, TMid4>,
    t5: Transformer<TOutputType, TMid4, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe<TIn, TMid1, TMid2, TMid3, TMid4, TMid5, TOut>(
    t1: Transformer<TOutputType, TIn, TMid1>,
    t2: Transformer<TOutputType, TMid1, TMid2>,
    t3: Transformer<TOutputType, TMid2, TMid3>,
    t4: Transformer<TOutputType, TMid3, TMid4>,
    t5: Transformer<TOutputType, TMid4, TMid5>,
    t6: Transformer<TOutputType, TMid5, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe<TIn, TMid1, TMid2, TMid3, TMid4, TMid5, TMid6, TOut>(
    t1: Transformer<TOutputType, TIn, TMid1>,
    t2: Transformer<TOutputType, TMid1, TMid2>,
    t3: Transformer<TOutputType, TMid2, TMid3>,
    t4: Transformer<TOutputType, TMid3, TMid4>,
    t5: Transformer<TOutputType, TMid4, TMid5>,
    t6: Transformer<TOutputType, TMid5, TMid6>,
    t7: Transformer<TOutputType, TMid6, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public pipe(
    ...transformers: Transformer<TOutputType>[]
  ): Transformer<TOutputType>
  public pipe(
    ...transformers: Transformer<TOutputType>[]
  ): Transformer<TOutputType> {
    return this.custom(input =>
      transformers.reduce(
        (curr, transformer) => transformer[this.methodName](curr),
        input,
      ),
    )
  }

  /**
   * Transforms an input value using the given transformer on the given field of
   * that input value
   *
   * Inverse of {@link nest}
   *
   * @example
   * interface Input {
   *   data: InputData
   * }
   * interface Output {
   *   ...
   * }
   * const dataTransformer = factory.object<InputData, Output>({ ... })
   * const inputTransformer = factory.select<Input, Output, 'data'>('data', dataTransformer)
   */
  public select<TIn, TOut, K extends Key.In.Union<TIn>>(
    key: K | ((input: TIn) => K),
    transformer:
      | Transformer<TOutputType, IndexUnion<TIn, K>, TOut | undefined>
      // NOTE: this is still needed, despite the above overload, since sometimes
      // we receive something that might be PASS_THROUGH or a transformer
      | PassThroughIfPossible<IndexUnion<TIn, K>, TOut | undefined>,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined>
  // NOTE: this overload is specifically for when we know it is a transformer.
  // It makes it easier for TypeScript to infer the proper types in a nested
  // context
  public select<TIn, K extends Key.In.Union<TIn>>(
    key: K | ((input: TIn) => K),
    transformer: typeof PASS_THROUGH,
  ): Transformer<TOutputType, TIn | Nullish, IndexUnion<TIn, K> | undefined>
  public select<TIn, TOut, K extends Key.In.Union<TIn>>(
    key: K | ((input: TIn) => K),
    transformer:
      | Transformer<TOutputType, IndexUnion<TIn, K>, TOut | undefined>
      | PassThroughIfPossible<IndexUnion<TIn, K>, TOut | undefined>,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined> {
    return this.nullable(
      this.custom(input => {
        const selection = input[
          typeof key === 'function' ? key(input) : key
        ] as IndexUnion<TIn, K>
        if (transformer === PASS_THROUGH) {
          return selection as TOut
        }
        return transformer[this.methodName](selection)
      }),
    )
  }

  /**
   * Transforms using the given transformer, and then takes the output and
   * creates an object with a single property where the value of that property
   * is the transformed output and the key of that property is given (or
   * produced through a callback)
   *
   * Inverse of {@link select}
   *
   * @example
   * interface Input {
   *   ...
   * }
   * interface Output {
   *   data: OutputData
   * }
   * const dataTransformer = factory.object<Input, OutputData>({ ... })
   * const inputTransformer = factory.nest<Input, Output, 'data'>('data', dataTransformer)
   */
  public nest<TIn, TOut, K extends keyof any>(
    key: K | ((input: TIn, output: TOut) => K),
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
    options: Replace.Properties.WithConstraint<
      Factory.nest.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    TIn,
    TOut extends Nullish ? undefined : FlattenUnion<OneOf<Record<K, TOut>>>
  >
  public nest<TIn, TOut, K extends keyof any>(
    key: K | ((input: TIn, output: TOut) => K),
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
    options: Replace.Properties.WithConstraint<
      Factory.nest.Options,
      'onNullish',
      'preserve'
    >,
  ): Transformer<TOutputType, TIn, FlattenUnion<OneOf<Record<K, TOut>>>>
  public nest<TIn, TOut, K extends keyof any>(
    key: K | ((input: TIn, output: TOut) => K),
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
    options?: Factory.nest.Options,
  ): Transformer<
    TOutputType,
    TIn,
    FlattenUnion<OneOf<Record<K, NullToUndefined<TOut>>>>
  >
  public nest(
    key: keyof any | ((input: unknown, output: unknown) => keyof any),
    transformer: Transformer<TOutputType> | typeof PASS_THROUGH,
    { onNullish = 'standardize' }: Factory.nest.Options = {},
  ): Transformer<TOutputType, unknown, undefined | Record<keyof any, unknown>> {
    return this.custom(input => {
      let output =
        transformer === PASS_THROUGH
          ? input
          : transformer[this.methodName](input)
      if (TypeChecker.isNullish(output)) {
        switch (onNullish) {
          case 'remove':
            return undefined
          case 'standardize':
            output = undefined
            break
          case null:
            output = null
            break
          case 'preserve':
            break
          default:
            throw TypeAsserter.assertIsNever(onNullish)
        }
      }
      const k = typeof key === 'function' ? key(input, output) : key
      return { [k]: output }
    })
  }

  /**
   * Builds a desired object by independently generating or transforming values
   * from a source value
   *
   * __NOTE:__ optional fields still require a transformer (or PASS_THROUGH) to
   * ensure that we don't forget a transformer for a given property
   */
  public object<TIn, TOut extends object>(
    fields: Factory.object.Fields<TOutputType, TIn, TOut>,
    options: Replace.Properties.WithConstraint<
      Factory.object.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    TIn | Nullish,
    Partial.Semi<TOut, Key.That.Accepts<Nullish, TOut>> | undefined
  >
  public object<TIn, TOut extends object>(
    fields: Factory.object.Fields<TOutputType, TIn, TOut>,
    options?: Factory.object.Options,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined>
  public object(
    fields: Factory.object.Fields<
      string,
      // NOTE: this type allows for `fieldTransform` below to be
      // `PASS_THROUGH`
      Unknown,
      Unknown.ButNotNullish
    >,
    {
      lazy = false,
      onNullish = 'standardize',
      onMissing = 'include',
    }: Factory.object.Options = {},
  ): Transformer {
    fields = { ...fields }
    const getters: Record<string, (input: NonNullable<unknown>) => unknown> = {}
    // Cleaned up so it doesn't cause an issue below
    for (const k in fields) {
      if (fields[k] !== undefined) {
        getters[k] = input => {
          const fieldTransform = fields[k]
          let fieldValue: unknown
          if (fieldTransform === PASS_THROUGH) {
            // If PASS_THROUGH, value of field on input is used as value of
            // field on output
            if (onMissing === 'omit' && !(k in input)) {
              return REMOVE
            }
            fieldValue = input[k as keyof typeof input]
          } else if (this.canUse(fieldTransform)) {
            if (onMissing === 'omit' && !(k in input)) {
              return REMOVE
            }
            let fieldInput = input[k as keyof typeof input]
            if (fieldInput === null && onNullish === 'standardize') {
              // Typically, we only handle nullish values after transformation.
              // However, in the case where someone is using a transformer on a
              // specific field and their desired behavior for `onNullish` is
              // `standardize`, they likely expect to only have to deal with
              // `undefined`. So we convert `null` to `undefined` in that case.
              // Note we don't need to do this in any of below the cases where
              // we use the full input object, as `nullable` already handles
              // nullish input objects. This only applies when we are passing a
              // field of the input object to a transformer/callback
              fieldInput = undefined as typeof fieldInput
            }
            // If fieldTransform is a transformer (for this outputType), we can
            // apply the transformer to the value of the field on the input
            fieldValue = fieldTransform[this.methodName](fieldInput)
          } else if (typeof fieldTransform === 'function') {
            // If fieldTransform is a function, we use it to transform the
            // entire input to the value of this field on the output
            fieldValue = fieldTransform(input)
          } else {
            const { usingFullInput } = fieldTransform as FlattenUnion<
              typeof fieldTransform
            >
            if (usingFullInput === PASS_THROUGH) {
              // If fieldTransform is a an object that indicates that we want to
              // passthrough full input, then we simply assign the full input to
              // this field
              fieldValue = input
            } else if (this.canUse(usingFullInput)) {
              // If fieldTransform is a an object with a transformer that
              // expects the full input, we use it to transform the entire input
              // to the value of this field on the output
              fieldValue = usingFullInput[this.methodName](input)
            } else {
              // The fields config has an invalid option. If this happens, there
              // is a bug in the code. So the only applicable user-friendly error
              // is an unexpected error. Thus, we don't need to throw a
              // user-friendly error since if one is needed, this will be caught
              // and turned into an unexpected error somewhere up the chain
              throw new TypeError(
                `'object' factory method was given an invalid transform property for ${format(
                  k,
                )}: ${format(fieldTransform)}`,
              )
            }
          }
          if (TypeChecker.isNullish(fieldValue)) {
            switch (onNullish) {
              case 'preserve':
                break
              case 'remove':
                fieldValue = REMOVE
                break
              case 'standardize':
                fieldValue = undefined
                break
              case null:
                fieldValue = null
                break
              default:
                throw TypeAsserter.assertIsNever(onNullish)
            }
          }
          return fieldValue
        }
      }
    }

    return this.nullable(
      this.custom((input: NonNullable<unknown>) => {
        const output: Record<string, unknown> = {}
        if (lazy) {
          const cache: Record<string, unknown> = {}
          for (const k in fields) {
            // Skip defining property if key is missing from input and onMissing
            // is 'omit' — this ensures the key is truly absent from the output
            // object, so `'key' in obj` returns false
            if (onMissing === 'omit' && !(k in (input as object))) {
              continue
            }
            // Defines a getter property that will be lazily transformed, just
            // once
            Object.defineProperty(output, k, {
              get: () => (cache[k] ??= getters[k](input)),
            })
          }
          Cache.attachKey(output, () => {
            // NOTE: we need to call each getter so that the cached fields are
            // all populated. Skip keys that were omitted above.
            for (const k in fields) {
              if (onMissing === 'omit' && !(k in (input as object))) {
                continue
              }
              // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
              output[k]
            }
            // We use the plain cached object rather than the object with
            // getters
            return cache
          })
        } else {
          for (const k in fields) {
            const v = getters[k](input)
            // REMOVE is a sentinel meaning "omit this key entirely" — skip the
            // assignment so the key is never present in the output
            if (v !== REMOVE) {
              output[k] = v
            }
          }
        }
        return output
      }),
    )
  }

  /**
   * Builds a desired tuple by independently transforming a source value
   *
   * REVIEW: the input or output types might not be inferred correctly in all cases?
   */
  public tuple<TIn, const TOut extends readonly unknown[]>(
    transformers: {
      [K in keyof TOut]:
        | Transformer<TOutputType, TIn, TOut[K]>
        | PassThroughIfPossible<NonNullable<TIn>, TOut[K]>
    },
    options: Replace.Properties.WithConstraint<
      Factory.tuple.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    TIn | Nullish,
    (TOut extends any ? NonNullable<TOut[keyof TOut]>[] : never) | undefined
  >
  public tuple<TIn, const TOut extends readonly unknown[]>(
    transformers: {
      [K in keyof TOut]:
        | Transformer<TOutputType, TIn, TOut[K]>
        | PassThroughIfPossible<NonNullable<TIn>, TOut[K]>
    },
    options?: Factory.tuple.Options,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined>
  public tuple<TIn, const TOut extends readonly unknown[]>(
    transformers: {
      [K in keyof TOut]:
        | Transformer<TOutputType, TIn, TOut[K]>
        | PassThroughIfPossible<NonNullable<TIn>, TOut[K]>
    },
    { onNullish = 'standardize' }: Factory.tuple.Options = {},
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined> {
    /** Used to discard nulls, if needed */
    const trash = Symbol()
    // Result is cast because TypeScript cannot make sense of the transformers
    // when using deferred evaluation
    return this.nullable(
      this.custom<TIn, TOut>(
        input =>
          transformers
            .map(transformer => {
              const value =
                transformer === PASS_THROUGH
                  ? input
                  : transformer[this.methodName](input)
              if (TypeChecker.isNullish(value)) {
                switch (onNullish) {
                  case 'preserve':
                    break
                  case 'remove':
                    return trash
                  case 'standardize':
                    return undefined
                  case null:
                    return null
                  default:
                    throw TypeAsserter.assertIsNever(onNullish)
                }
              }
              return value
            })
            .filter(
              <T>(val: T | typeof trash): val is T => val !== trash,
            ) as readonly unknown[] as TOut,
      ),
    )
  }

  /**
   * Transforms each item in the list with the given transformer
   */
  public list<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    options: Replace.Properties.WithConstraint<
      Factory.list.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    NonNullable<TOut>[] | undefined
  >
  public list<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
    options?: Factory.list.Options,
  ): Transformer<TOutputType, readonly TIn[] | Nullish, TOut[] | undefined>
  public list<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
    // NOTE: while 'remove' might make sense here, it does not in the other
    // places where we have `onNullish`, so for consistency, we keep it as
    // 'standardize'. Plus, there are some cases that might expect nullish
    // values and be surprised when they are removed
    { onNullish = 'standardize' }: Factory.list.Options = {},
  ): Transformer<TOutputType, readonly TIn[] | Nullish, TOut[] | undefined> {
    /** Used to discard nulls, if needed */
    const trash = Symbol()
    return this.nullable(
      this.custom(input =>
        input
          ?.map(inItem => {
            const outItem =
              transformer === PASS_THROUGH
                ? (inItem as unknown as TOut)
                : transformer[this.methodName](inItem)
            if (TypeChecker.isNullish(outItem)) {
              switch (onNullish) {
                case 'preserve':
                  break
                case 'remove':
                  return trash
                case 'standardize':
                  return undefined as typeof outItem
                case null:
                  return null as typeof outItem
                default:
                  throw TypeAsserter.assertIsNever(onNullish)
              }
            }
            return outItem
          })
          .filter(<T>(val: T | typeof trash): val is T => val !== trash),
      ),
    )
  }

  /**
   * Transforms each item in the list with the given transformer
   */
  public listFromRecord<
    TIn extends Record<string, any>,
    TKeyProp extends string,
    TValProp extends string,
  >(
    keyProp: TKeyProp,
    valProp: TValProp,
    options: Replace.Properties.WithConstraint<
      Factory.listFromRecord.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    TIn | Nullish,
    | (Record<TKeyProp, Key.In.Union<TIn>> &
        Record<TValProp, NonNullable<IndexUnion<TIn>>>)[]
    | undefined
  >
  public listFromRecord<
    TIn extends Record<string, any>,
    TKeyProp extends string,
    TValProp extends string,
  >(
    keyProp: TKeyProp,
    valProp: TValProp,
    options?: Factory.listFromRecord.Options,
  ): Transformer<
    TOutputType,
    TIn | Nullish,
    | (Record<TKeyProp, Key.In.Union<TIn>> &
        Record<TValProp, IndexUnion<TIn>>)[]
    | undefined
  >
  public listFromRecord(
    keyProp: string,
    valProp: string,
    // NOTE: while 'remove' might make sense here, it does not in the other
    // places where we have `onNullish`, so for consistency, we keep it as
    // 'standardize'. Plus, there are some cases that might expect nullish
    // values and be surprised when they are removed
    { onNullish = 'standardize' }: Factory.listFromRecord.Options = {},
  ): Transformer<
    TOutputType,
    Record<string, unknown> | Nullish,
    Record<string, unknown>[] | undefined
  > {
    /** Used to discard nulls, if needed */
    const trash = Symbol()
    return this.nullable(
      this.custom(
        input =>
          input &&
          Object.entries(input)
            .map(([key, value]) => {
              if (TypeChecker.isNullish(value)) {
                switch (onNullish) {
                  case 'preserve':
                    break
                  case 'remove':
                    return trash
                  case 'standardize':
                    value = undefined
                    break
                  case null:
                    value = null
                    break
                  default:
                    throw TypeAsserter.assertIsNever(onNullish)
                }
              }
              return {
                [keyProp]: key,
                [valProp]: value,
              }
            })
            .filter(<T>(val: T | typeof trash): val is T => val !== trash),
      ),
    )
  }

  /**
   * Creates a Map by transforming each item in a list into an entry in the Map
   */
  public record<TIn, const TOutK extends keyof any, TOutV>(
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK | Nullish, TOutV | Nullish] | Nullish
    >,
    options: Replace.Properties.WithConstraint<
      Factory.record.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    Record<NonNullable<TOutK>, NonNullable<TOutV>> | undefined
  >
  // TODO: might need more overloads for when onNullish is set for key/value
  public record<TIn, TOutK extends keyof any, TOutV>(
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK, TOutV] | Nullish
    >,
    options?: Factory.record.Options,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    Record<TOutK, TOutV> | undefined
  >
  public record<TIn, TOutK extends keyof any, TOutV>(
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK, TOutV] | Nullish
    >,
    options?: Factory.record.Options,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    Record<TOutK, TOutV> | undefined
  > {
    return this._usingKeyValuePairs(
      keyValuePairs =>
        keyValuePairs.reduce(
          (prev, [k, v]) => Object.assign(prev, { [k]: v }),
          {} as Record<TOutK, TOutV>,
        ),
      transformer,
      options,
    )
  }

  /**
   * Creates a Map by transforming each item in a list into an entry in the Map
   */
  public map<TIn, TOutK, TOutV>(
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK | Nullish, TOutV | Nullish] | Nullish
    >,
    options: Replace.Properties.WithConstraint<
      Factory.map.Options,
      'onNullish',
      'remove'
    >,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    Map<NonNullable<TOutK>, NonNullable<TOutV>> | undefined
  >
  // TODO: might need more overloads for when onNullish is set for key/value
  public map<TIn, TOutK, TOutV>(
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK, TOutV] | Nullish
    >,
    options?: Factory.map.Options,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    Map<TOutK, TOutV> | undefined
  >
  public map<TIn, TOutK, TOutV>(
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK, TOutV] | Nullish
    >,
    options?: Factory.map.Options,
  ): Transformer<
    TOutputType,
    readonly TIn[] | Nullish,
    Map<TOutK, TOutV> | undefined
  > {
    return this._usingKeyValuePairs(
      keyValuePairs => new Map(keyValuePairs),
      transformer,
      options,
    )
  }

  /**
   * Creates an instance of the given class by first transforming the input into
   * constructor parameters for that class and then using those parameters to
   * instantiate the class
   *
   * @example
   * // Single constructor parameter that is not an array
   * const paramsTransformer = factory.object(...)
   * const classTransformer = factory.instance(MyClass, paramsTransformer)
   * // Single constructor parameter that is an array
   * const paramsTransformer = factory.tuple(factory.list(...))
   * const classTransformer = factory.instance(MyClass, paramsTransformer)
   * // Multiple constructor parameters
   * const paramsTransformer = factory.tuple(factory.object(...), ...)
   * const classTransformer = factory.instance(MyClass, paramsTransformer)
   */
  public instance<TIn, TOutCtor extends new (...args: any) => any>(
    ctor: TOutCtor,
    transformer:
      | Transformer<
          TOutputType,
          TIn,
          | Factory.instance.ConstructorParams<TOutCtor>
          // We always accept undefined because that tells us we don't need an
          // instance, we should just return undefined
          | undefined
        >
      | PassThroughIfPossible<
          NonNullable<TIn>,
          Factory.instance.ConstructorParams<TOutCtor>
        >,
  ): Transformer<TOutputType, TIn | Nullish, InstanceType<TOutCtor> | undefined>
  public instance(
    ctor: new (...args: readonly unknown[]) => unknown,
    transformer:
      | Transformer<TOutputType, unknown, unknown>
      | typeof PASS_THROUGH,
  ): Transformer<TOutputType, unknown | Nullish, unknown | undefined> {
    return this.nullable(
      this.custom(input => {
        if (transformer === PASS_THROUGH) {
          return input
        }
        const args = transformer[this.methodName](input)
        if (args === undefined) {
          return undefined
        }
        return new ctor(...pack(args))
      }),
    )
  }

  /**
   * Transforms from one enum to another
   */
  public enumeration<
    TIn extends string | number | symbol,
    TOut extends string | number | symbol,
  >(
    mapping: Record<TIn, TOut>,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined> {
    return this.nullable(this.custom<TIn, TOut>(enumIn => mapping[enumIn]))
  }

  /**
   * Parses a JSON string
   *
   * For the reverse operation, see {@link stringify}
   */
  public json({
    onNullish = 'standardize',
  }: Readonly<Factory.json.Options> = {}): Transformer<
    TOutputType,
    string | Nullish,
    unknown
  > {
    return this.nullable(
      this.custom(input => {
        const ret: unknown = JSON.parse(input)
        // Processes nullish values
        if (onNullish !== 'preserve') {
          if (TypeChecker.isNullish(ret)) {
            // At the top level, standardize to the chosen nullish value
            return onNullish === null ? null : undefined
          }
          if (TypeChecker.isObject(ret)) {
            // For objects (including arrays), we iteratively go through the
            // entire object processing all nested nullish properties
            const objs = [ret]
            let obj: object | undefined
            while ((obj = objs.pop())) {
              for (const _key in obj) {
                const key = _key as keyof typeof obj
                const val = obj[key]
                if (TypeChecker.isNullish(val)) {
                  switch (onNullish) {
                    case 'remove':
                      delete obj[key]
                      break
                    case 'standardize':
                      obj[key] = undefined as typeof val
                      break
                    case null:
                      obj[key] = null as typeof val
                      break
                    default:
                      throw TypeAsserter.assertIsNever(onNullish)
                  }
                } else if (TypeChecker.isObject(val)) {
                  // Sets the child object up to have its nullish properties
                  // processed
                  objs.push(val)
                }
              }
            }
          }
        }
        return ret
      }),
    )
  }

  /**
   * Converts a given value into a string
   */
  public stringify<T>({
    onNullish = 'remove',
    stringifier = 'json',
  }: Readonly<Factory.stringify.Options<T>> = {}): Transformer<
    TOutputType,
    T | Nullish,
    string | undefined
  > {
    return this.nullable(
      this.custom(input => {
        switch (stringifier) {
          case 'json':
            return JSON.stringify(
              input,
              // We just need to replace `null` values with `undefined` and
              // remove all nullish values from arrays. JSON.stringify does the
              // rest
              onNullish === 'remove'
                ? (k, v: unknown) =>
                    TypeChecker.isReadonlyArray(v)
                      ? v.filter(TypeChecker.isNotNullish.raw)
                      : (v ?? undefined)
                : // Typically, 'standardize' converts `null` => `undefined`.
                  // Here, we do the reverse because JSON strings can only contain
                  // `null`, not `undefined`. Converting `null` => `undefined` is
                  // effectively what the logic above for 'remove' does
                  onNullish === 'standardize'
                  ? (k, v: unknown) => v ?? null
                  : // onNullish === 'preserve' => we don't use any replacer
                    undefined,
            )
          case 'internal':
            return input?.toString()
          default:
            return stringifier(input) ?? undefined
        }
      }),
    )
  }

  /**
   * Throws error when receiving or outputting nullish
   */
  public nonNull<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, NonNullable<TOut>>,
    { warnOnly = false }: Factory.nonNull.Options = {},
  ): Transformer<TOutputType, NonNullable<TIn>, NonNullable<TOut>> {
    return this.custom(input => {
      input = (
        warnOnly ? TypeAsserter.validateButJustWarn : TypeAsserter.validate
      )(input, TypeAsserter.assertIsNotNullish)
      if (transformer === PASS_THROUGH) {
        return input as unknown as NonNullable<TOut>
      }
      const output = transformer[this.methodName](input)
      TypeAsserter.assertIsNotNullish(output)
      return output
    })
  }

  /**
   * When receiving nullish, returns undefined. Otherwise, uses the given
   * transformer
   */
  public nullable<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut | undefined>,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined> {
    return this.custom(input => {
      if (TypeChecker.isNullish(input)) {
        return undefined
      }
      if (transformer === PASS_THROUGH) {
        return input as unknown as TOut
      }
      return transformer[this.methodName](input)
    })
  }

  /**
   * Checks the value of the input before deciding which transformer to use
   *
   * @see {@link Factory.discriminateOn}
   */
  public if<TInTrue, TInFalse, TOut>(
    predicate: (input: unknown) => input is TInTrue,
    trueTransformer:
      | Transformer<TOutputType, TInTrue, TOut>
      | PassThroughIfPossible<TInTrue, TOut>,
    falseTransformer:
      | Transformer<TOutputType, TInFalse, TOut>
      | PassThroughIfPossible<TInFalse, TOut>,
  ): Transformer<TOutputType, TInTrue | TInFalse, TOut>
  public if<TIn, TCheck, TOut>(
    predicate: (input: unknown) => input is TCheck,
    trueTransformer:
      | Transformer<TOutputType, TIn & TCheck, TOut>
      | PassThroughIfPossible<TIn & TCheck, TOut>,
    falseTransformer:
      | Transformer<TOutputType, Exclude<TIn, TCheck>, TOut>
      | PassThroughIfPossible<Exclude<TIn, TCheck>, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public if<TIn, TCheck extends TIn, TOut>(
    predicate: (input: TIn) => input is TCheck,
    trueTransformer:
      | Transformer<TOutputType, TIn & TCheck, TOut>
      | PassThroughIfPossible<TIn & TCheck, TOut>,
    falseTransformer:
      | Transformer<TOutputType, Exclude<TIn, TCheck>, TOut>
      | PassThroughIfPossible<Exclude<TIn, TCheck>, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public if<TIn, TOut>(
    predicate: (input: TIn) => boolean,
    trueTransformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
    falseTransformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<TIn, TOut>,
  ): Transformer<TOutputType, TIn, TOut>
  public if<TIn, TCheck>(
    predicate: (input: unknown) => boolean,
    trueTransformer: Transformer<TOutputType> | typeof PASS_THROUGH,
    // NOTE: for some weird reason, overloads don't match the implementation
    // unless we have `Exclude<TIn, TCheck>`
    falseTransformer:
      | Transformer<TOutputType, Exclude<TIn, TCheck>, unknown>
      | typeof PASS_THROUGH,
  ): Transformer<TOutputType, TIn> {
    return this.custom(input =>
      predicate(input)
        ? trueTransformer === PASS_THROUGH
          ? input
          : trueTransformer[this.methodName](input)
        : falseTransformer === PASS_THROUGH
          ? input
          : // NOTE: this casting is needed because of the weird bug mentioned
            // above
            falseTransformer[this.methodName](input as Exclude<TIn, TCheck>),
    )
  }

  /**
   * When the input type is a
   * {@link https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#discriminating-unions discriminated union},
   * you may want to use different transformers for different members of the
   * union. This factory method allows you to do so by specifying which property
   * to discriminate on and then providing transformers for each possible value
   * of that discriminator
   *
   * @see {@link Factory.if}
   * @example
   * type Input =
   *   | { __typename: 'Type1', a: 1 }
   *   | { __typename: 'Type2', b: 2 }
   *   | { __typename: 'Type3', c: 3 }
   * type Output =
   *   | { a: 1 }
   *   | { b: 2 }
   *   | { c: 3 }
   * const transformer = factory.discriminateOn<Input, Output, '__typename'>('__typename', {
   *   Type1: factory.object({ a: PASS_THROUGH }),
   *   Type2: factory.object({ b: PASS_THROUGH }),
   *   Type3: factory.object({ c: PASS_THROUGH }),
   * })
   */
  public discriminateOn<
    TIn,
    TOut,
    TDiscriminatorKey extends keyof NonNullable<TIn>,
  >(
    discriminatorKey: TDiscriminatorKey,
    transformers: {
      readonly [TDiscriminatorValue in NonNullable<TIn>[TDiscriminatorKey] &
        keyof any]: Transformer<
        TOutputType,
        NonNullable<TIn> & Record<TDiscriminatorKey, TDiscriminatorValue>,
        TOut | undefined
      >
    },
    defaultTransformer?: Transformer<
      TOutputType,
      NonNullable<TIn>,
      TOut | undefined
    >,
  ): Transformer<TOutputType, TIn | Nullish, TOut | undefined> {
    return this.nullable(
      this.custom((input: NonNullable<TIn>): TOut | undefined => {
        const discriminatorValue = input[discriminatorKey]
        const transformer =
          // NOTE: all of this casting is because we are using type parameters
          (transformers[
            discriminatorValue as keyof typeof transformers
          ] satisfies Transformer<
            TOutputType,
            any,
            TOut | undefined
          > as Transformer<TOutputType, NonNullable<TIn>, TOut | undefined>) ??
          defaultTransformer
        TypeAsserter.assertIsNotNullish(transformer)
        return transformer[this.methodName](input)
      }),
      // NOTE: Typescript doesn't understand that `NonNullable<TIn> |
      // Nullish` and `TIn | Nullish` have exactly the same meaning.
    ) satisfies Transformer<
      TOutputType,
      NonNullable<TIn> | Nullish,
      TOut | undefined
    > as Transformer<TOutputType, TIn | Nullish, TOut | undefined>
  }

  /**
   * Inspired by
   * {@link https://github.com/graphql/graphql-spec/pull/825 | GraphQL's oneOf input objects},
   * this allows you to transform an object where you only expect one of the the
   * object's properties to be defined
   *
   * __NOTE:__ if multiple properties are defined on an input object, an
   * applicable transformer will be selected at random
   *
   * @example
   * type Input = ExactlyOneOf<{ bird: Bird, cat: Cat, dog: Dog }>
   * type Output = Animal
   * const transformer = factory.oneOf<Input, Output>({
   *   bird: factory.custom<Bird, Animal>(...),
   *   cat: factory.custom<Cat, Animal>(...),
   *   dog: factory.custom<Dog, Animal>(...),
   * })
   */
  public oneOf<TIn, TOut>(transformers: {
    readonly [K in Key.In.Union<TIn>]: Transformer<
      TOutputType,
      NonNullable<IndexUnion<TIn, K>>,
      TOut | undefined
    >
  }): Transformer<TOutputType, TIn | Nullish, TOut | undefined> {
    return this.nullable(
      this.custom((input: NonNullable<TIn>): TOut | undefined => {
        for (const k in input) {
          type Key = keyof typeof transformers
          const val = input[k] as NonNullable<IndexUnion<TIn, Key>>
          // NOTE: casting is used here because TypeScript is not able to assess
          // what `transformers` type actually means (deferred evaluation)
          const transformer = transformers[k satisfies string as string as Key]
          if (TypeChecker.isNotNullish(val) && transformer) {
            return transformer[this.methodName](val)
          }
        }
        return undefined
      }),
      // NOTE: Typescript doesn't understand that `NonNullable<TIn> |
      // Nullish` and `TIn | Nullish` have exactly the same meaning.
    ) satisfies Transformer<
      TOutputType,
      NonNullable<TIn> | Nullish,
      TOut | undefined
    > as Transformer<TOutputType, TIn | Nullish, TOut | undefined>
  }

  /**
   * Uses the given transformer, but replaces nullish outputs with the given
   * default value
   */
  // NOTE: this overload tells TypeScript if the default value can be inferred
  // to match the non-nullable output value, it should
  public default<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultVal: NonNullable<TOut>,
  ): Transformer<TOutputType, TIn, NonNullable<TOut>>
  // NOTE: this overload tells TypeScript if the default value can be inferred
  // to match the output value, it should
  public default<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultVal: TOut,
  ): Transformer<TOutputType, TIn, TOut>
  // NOTE: this overload allows any default value to be used
  public default<TIn, TOut, TDefault>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultVal: TDefault,
  ): Transformer<TOutputType, TIn, NonNullable<TOut> | TDefault>
  public default<TIn, TOut, TDefault>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultVal: TDefault,
  ): Transformer<TOutputType, TIn, NonNullable<TOut> | TDefault> {
    return this.custom(
      input =>
        (transformer === PASS_THROUGH
          ? (input as unknown as TOut)
          : transformer[this.methodName](input)) ?? defaultVal,
    )
  }

  /**
   * Uses the given transformer, but replaces nullish outputs with the given
   * default value
   */
  // NOTE: this overload tells TypeScript if the default value can be inferred
  // to match the non-nullable output value, it should
  public lazyDefault<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultValGenerator: () => NonNullable<TOut>,
  ): Transformer<TOutputType, TIn, NonNullable<TOut>>
  // NOTE: this overload tells TypeScript if the default value can be inferred
  // to match the output value, it should
  public lazyDefault<TIn, TOut>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultValGenerator: () => TOut,
  ): Transformer<TOutputType, TIn, TOut>
  // NOTE: this overload allows any default value to be used
  public lazyDefault<TIn, TOut, TDefault>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultValGenerator: () => TDefault,
  ): Transformer<TOutputType, TIn, NonNullable<TOut> | TDefault>
  public lazyDefault<TIn, TOut, TDefault>(
    transformer:
      | Transformer<TOutputType, TIn, TOut>
      | PassThroughIfPossible<NonNullable<TIn>, TOut>,
    defaultValGenerator: () => TDefault,
  ): Transformer<TOutputType, TIn, NonNullable<TOut> | TDefault> {
    return this.custom(
      input =>
        (transformer === PASS_THROUGH
          ? (input as unknown as TOut)
          : transformer[this.methodName](input)) ?? defaultValGenerator(),
    )
  }

  /**
   * Does not necessarily transform the value to the desired type. Instead, this
   * uses a {@link TypeAsserter} to "ensure" it is the valid type
   *
   * **NOTE:** there are some cases where you don't actually want to throw an
   * error if the {@link TypeAsserter} is not met; e.g., when receiving an API
   * response on the frontend, you may want to proceed and try to render the
   * results even if there are some discrepancies in the type. In those cases,
   * you can pass `warnOnly: true` to cause this transformer to log warnings for
   * invalid values rather than throw errors.
   *
   * @example
   * interface Input {
   *   id: string | number
   * }
   * interface Output {
   *   id: string
   * }
   * const transformer = factory.object<Input, Output>({
   *   id: factory.validate(TypeAsserter.string),
   * })
   */
  public validate<TIn, TOut>(
    transformer: typeof PASS_THROUGH,
    asserter: TypeAsserter<TOut, TIn>,
    options?: Factory.validate.Options,
  ): Transformer<TOutputType, TIn, TOut>
  public validate<TIn, TAssert>(
    transformer: typeof PASS_THROUGH,
    asserter: TypeAsserter<TAssert, TIn>,
    options?: Factory.validate.Options,
  ): Transformer<TOutputType, TIn, TIn & TAssert>
  // NOTE: This is a common use case that the other overloads do not handle
  // properly
  public validate<TIn, TOut>(
    transformer: Transformer<TOutputType, TIn, TOut>,
    asserter: typeof TypeAsserter.assertIsNotNullish,
    options?: Factory.validate.Options,
  ): Transformer<TOutputType, TIn, NonNullable<TOut>>
  public validate<TIn, TOut, TMid>(
    transformer: Transformer<TOutputType, TIn, TMid>,
    asserter: TypeAsserter<TOut, TMid>,
    options?: Factory.validate.Options,
  ): Transformer<TOutputType, TIn, TOut>
  public validate<TIn, TMid, TAssert>(
    transformer: Transformer<TOutputType, TIn, TMid>,
    asserter: TypeAsserter<TAssert, TMid>,
    options?: Factory.validate.Options,
  ): Transformer<TOutputType, TIn, TMid & TAssert>
  public validate(
    transformer: Transformer<TOutputType> | typeof PASS_THROUGH,
    asserter: TypeAsserter<unknown>,
    { warnOnly = false }: Factory.validate.Options = {},
  ): Transformer<TOutputType> {
    const validate = warnOnly
      ? TypeAsserter.validateButJustWarn
      : TypeAsserter.validate
    return this.custom(input =>
      validate(
        transformer === PASS_THROUGH
          ? input
          : transformer[this.methodName](input),
        asserter,
      ),
    )
  }

  /**
   * Always returns the given value
   */
  public always<TOut>(output: TOut): Transformer<TOutputType, any, TOut> {
    return this.custom(() => output)
  }

  /**
   * Always throws the given error
   */
  public throw(error?: Error): Transformer<TOutputType, any, never> {
    return this.custom(val => {
      throw (
        error ?? new Error(`Cannot transform the given input: ${format(val)}`)
      )
    })
  }

  /**
   * Allows you to use a transformer that does not yet exist, but will exist
   * before a transformation is required
   *
   * @example
   *
   * const transformer1 = factory.object({
   *   // Cannot just use `transformer2` because it does not exist yet
   *   prop: factory.lazy(() => transformer2)
   * })
   * const transformer2 = factory.custom(...)
   */
  public lazy<TIn, TOut>(
    producer: () => Transformer<TOutputType, TIn, TOut>,
  ): Transformer<TOutputType, TIn, TOut> {
    let transformer: Transformer<TOutputType, TIn, TOut> | undefined
    return this.custom(input =>
      (transformer ??= producer())[this.methodName](input),
    )
  }

  /**
   * Transforms input into a list of key-value pairs and then aggregates those
   * key-value pairs into some aggregate type (e.g. {@link Record} or
   * {@link Map})
   */
  private _usingKeyValuePairs<TIn, TOutK, TOutV, TAggr>(
    aggregator: (keyValuePairs: readonly (readonly [TOutK, TOutV])[]) => TAggr,
    transformer: Transformer<
      TOutputType,
      TIn,
      readonly [TOutK, TOutV] | Nullish
    >,
    {
      onNullish = 'standardize',
    }: Factory.ElementOptions.ForKeyValuePairing = {},
  ): Transformer<TOutputType, readonly TIn[] | Nullish, TAggr | undefined> {
    // Determine how to handle nullish keys and how to handle nullish values.
    // NOTE: `null` must be checked before the `typeof === 'string'` branch
    // because `typeof null === 'object'`, which would incorrectly fall into
    // the per-key/value object branch and crash on `null.key`
    const onNullishForKey =
      onNullish === null || typeof onNullish === 'string'
        ? onNullish
        : (onNullish.key ?? 'standardize')
    const onNullishForValue =
      onNullish === null || typeof onNullish === 'string'
        ? onNullish
        : (onNullish.value ?? 'standardize')
    return this.nullable(
      this.custom(input =>
        aggregator(
          input
            .map(inItem => {
              const keyValuePair = transformer[this.methodName](inItem)
              if (!keyValuePair) {
                // If the entire pair is nullish, we can ignore it
                return undefined
              }
              let [k, v] = keyValuePair
              if (TypeChecker.isNullish(k)) {
                switch (onNullishForKey) {
                  case 'preserve':
                    break
                  case 'remove':
                    // Filtered out below
                    return undefined
                  case 'standardize':
                    k = undefined as TOutK
                    break
                  case null:
                    k = null as TOutK
                    break
                  default:
                    throw TypeAsserter.assertIsNever(onNullishForKey)
                }
              }
              if (TypeChecker.isNullish(v)) {
                switch (onNullishForValue) {
                  case 'preserve':
                    break
                  case 'remove':
                    // Filtered out below
                    return undefined
                  case 'standardize':
                    v = undefined as TOutV
                    break
                  case null:
                    v = null as TOutV
                    break
                  default:
                    throw TypeAsserter.assertIsNever(onNullishForValue)
                }
              }
              return [k, v] as const
            })
            .filter(TypeChecker.isNotNullish.raw),
        ),
      ),
    )
  }
}
export namespace Factory {
  export type OutputType<TFactory extends Factory<any>> =
    TFactory extends Factory<infer TOutputType> ? TOutputType : never

  /* ----------------------------- Method Options ----------------------------- */
  export namespace nest {
    export type Options = ElementOptions
  }
  export namespace object {
    export type Fields<
      TOutputType extends string,
      TIn,
      TOut extends Object.Constraint,
    > = {
      [K in Key.In.Union<TOut>]:  // All to transform the field
        | Function<TIn, IndexUnion<TOut, K>>
        | (K extends Key.In.Union<TIn>
            ? // If field exists on TIn, allows transformer that will be applied
              // on the value of that field
              | Transformer<
                    TOutputType,
                    IndexUnion<TIn, K>,
                    IndexUnion<TOut, K>
                  >
                // And if field on TIn can be used for TOut, allow the PASS_THROUGH symbol
                | PassThroughIfPossible<
                    Coalesce<IndexUnion.WherePresent<TIn, K>, undefined>,
                    IndexUnion<TOut, K>
                  >
            : never)
        | {
            /**
             * In certain cases, an output field doesn't correspond to an input
             * field, but rather to another field on the input object or to the
             * input object as a whole. In those cases, this can be used.
             *
             * @example
             * // Reference another field
             * factory.object({
             *   someField: {
             *     usingFullInput: factory.select('someOtherField', PASS_THROUGH)
             *   }
             * })
             *
             * // Transform the whole object into a field
             * // NOTE: if this is the only field on the object, we can use `nest` instead
             * factory.object({
             *   info: {
             *     usingFullInput: factory.object({ ... })
             *   },
             *   ...
             * })
             */
            usingFullInput:
              | Transformer<TOutputType, TIn, IndexUnion<TOut, K>>
              | PassThroughIfPossible<TIn, IndexUnion<TOut, K>>
          }
    }

    export interface Options extends ElementOptions {
      /**
       * If `true`, the properties of the object are not transformed until
       * something references that property. This can be useful to defer a
       * massive amount of transformation up front.
       *
       * @default false
       */
      lazy?: boolean
      /**
       * Determines how fields whose input value is absent (key not present in
       * input) are handled
       *
       * - `include` - Include the key in the output (default behaviour)
       * - `omit` - Omit the key from the output entirely when the key is not
       *   present in the input. Useful when consumers distinguish between a key
       *   being absent vs present-but-undefined (e.g. `'key' in obj`).
       *
       * @default 'include'
       */
      onMissing?: 'include' | 'omit'
    }
  }
  export namespace tuple {
    export type Options = ElementOptions
  }
  export namespace list {
    export type Options = ElementOptions
  }
  export namespace listFromRecord {
    export type Options = ElementOptions
  }
  export namespace record {
    export type Options = ElementOptions.ForKeyValuePairing
  }
  export namespace map {
    export type Options = ElementOptions.ForKeyValuePairing
  }
  export namespace instance {
    export type ConstructorParams<TCtor extends new (...args: any) => any> =
      | // Readonly is more flexible
      Readonly<ConstructorParameters<TCtor>>
      // If a single constructor argument (that is not an array) is accepted,
      // then this transformer can produce that single argument directly rather
      // than product a list with just one argument
      | ([ConstructorParameters<TCtor>[0]] extends ConstructorParameters<TCtor>
          ? // We exclude arrays because we can't differentiate a single array
            // argument from an array of multiply arguments, and we exclude
            // undefined because we can't differentiate a single undefined
            // argument from the case where we actually want to return undefined
            // instead of an instance
            Exclude<ConstructorParameters<TCtor>[0], readonly any[] | undefined>
          : never)
  }
  export namespace json {
    export type Options = ElementOptions
  }
  export namespace stringify {
    export interface Options<T> extends ElementOptions {
      /**
       * Used to convert a given value to a string
       *
       * Can be a custom callback or one of the following standard options:
       * - `json` => use {@link JSON.stringify}
       * - `internal` => use `toString()`
       *
       * @default 'json'
       */
      stringifier?: 'json' | 'internal' | ((val: T) => string | Nullish)
    }
  }
  export namespace nonNull {
    export type Options = ValidationOptions
  }
  export namespace validate {
    export type Options = ValidationOptions
  }

  /* ----------------------------- Common Options ----------------------------- */

  export interface ValidationOptions {
    /**
     * When `true`, an error won't actually be thrown on invalid input. Instead,
     * a warning will be logged, but the input will still be used.
     *
     * @default false
     */
    warnOnly?: boolean
  }

  /**
   * These options apply to transformers that invoke other transformers on
   * multiple elements (e.g. list transforming each item or an object
   * transforming each property)
   */
  export interface ElementOptions {
    /**
     * Determines how {@link Nullish} (i.e. `null` and `undefined`) values are
     * handled
     *
     * - `remove` - Omits nullish values
     * - `preserve` - Leave nullish values as they are
     * - `standardize` - Converts all nullish values to `undefined`
     * - `null` - Converts all nullish values to `null`
     *
     * @default Usually, 'standardize'
     */
    onNullish?: 'standardize' | 'remove' | 'preserve' | null
  }
  export namespace ElementOptions {
    export type ForKeyValuePairing = {
      [K in keyof ElementOptions]:
        | ElementOptions[K]
        | {
            key?: ElementOptions['onNullish']
            value?: ElementOptions['onNullish']
          }
    }
  }
}

/**
 * Used to indicate a property should be removed from the result
 */
const REMOVE = Symbol()
